import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Globe, AtSign, Lock, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getHomeRoute } from '../utils/homeRoute';
import BrandLockup from '../components/BrandLockup';

interface LoginForm {
  identifier: string;
  password: string;
}

const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string>('');
  const { login, authActionLoading } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    defaultValues: {
      identifier: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      setLoginError(''); // Clear any previous errors
      console.log('🚀 Starting login process...');
      const userData = await login(data.identifier, data.password);
      navigate(getHomeRoute(userData));
    } catch (error: any) {
      console.error('❌ Login failed in component:', error);
      // Form values are automatically preserved by React Hook Form
      setLoginError(error.response?.data?.message || t('auth.loginError'));
    }
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const highlights = [
    t('auth.highlightMonitoring'),
    t('auth.highlightOperations'),
    t('auth.highlightWorkforce'),
  ];

  return (
    <div className="min-h-screen flex bg-white">
      {/* ── Brand panel (desktop) ── */}
      <div className="relative hidden lg:flex lg:w-[46%] xl:w-1/2 overflow-hidden text-white bg-gradient-to-br from-[#0060b9] via-[#0055a9] to-[#002344]">
        {/* decorative glows */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 22% 18%, rgba(0,165,189,0.38), transparent 52%), radial-gradient(circle at 85% 90%, rgba(0,138,187,0.30), transparent 46%)',
          }}
        />
        {/* oversized faint equalizer motif */}
        <svg
          className="pointer-events-none absolute -bottom-16 -right-10 w-[420px] opacity-[0.08]"
          viewBox="0 0 64 64"
          fill="none"
          aria-hidden
        >
          <rect x="3" y="8" width="8" height="24" rx="4" fill="#ffffff" />
          <rect x="15.5" y="8" width="8" height="36" rx="4" fill="#ffffff" />
          <rect x="28" y="8" width="8" height="48" rx="4" fill="#ffffff" />
          <rect x="40.5" y="8" width="8" height="36" rx="4" fill="#ffffff" />
          <rect x="53" y="8" width="8" height="24" rx="4" fill="#ffffff" />
        </svg>

        <div className="relative z-10 flex w-full flex-col justify-between p-12 xl:p-16">
          <BrandLockup variant="light" size={44} />

          <div className="max-w-md">
            <h1 className="text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight">
              {t('auth.brandHeadline')}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-white/70">
              {t('auth.brandSubtitle')}
            </p>
            <ul className="mt-9 space-y-3.5">
              {highlights.map((line) => (
                <li key={line} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/15">
                    <Check className="h-3.5 w-3.5 text-white" aria-hidden />
                  </span>
                  <span className="text-sm text-white/90">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-sm text-white/45">
            © {new Date().getFullYear()} VueNexa Enterprise
          </p>
        </div>
      </div>

      {/* ── Form panel ── */}
      <div className="flex flex-1 flex-col min-h-screen">
        {/* Language selector */}
        <div className="flex justify-end p-5 sm:p-6">
          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
            <Globe className="h-4 w-4 text-gray-400" aria-hidden />
            <label htmlFor="lang" className="sr-only">
              {t('common.language')}
            </label>
            <select
              id="lang"
              value={i18n.language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="cursor-pointer border-0 bg-transparent pr-1 text-sm font-medium text-gray-700 focus:outline-none focus:ring-0"
            >
              <option value="en">English</option>
              <option value="gu">ગુજરાતી</option>
              <option value="hi">हिन्दी</option>
            </select>
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center px-6 pb-12">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="mb-8 flex justify-center lg:hidden">
              <BrandLockup variant="dark" size={38} />
            </div>

            <div className="mb-8 text-center lg:text-left">
              <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                {t('auth.loginTitle')}
              </h2>
              <p className="mt-1.5 text-sm text-gray-500">{t('auth.loginSubtitle')}</p>
            </div>

            {loginError && (
              <div
                role="alert"
                className="mb-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
              >
                <div className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-red-500" />
                <div>
                  <p className="text-sm font-semibold text-red-800">{t('auth.loginError')}</p>
                  <p className="mt-0.5 text-sm text-red-700">{loginError}</p>
                </div>
              </div>
            )}

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Email or phone */}
              <div>
                <label htmlFor="identifier" className="mb-1.5 block text-sm font-medium text-gray-700">
                  {t('auth.emailOrPhone')}
                </label>
                <div className="relative">
                  <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                  <input
                    id="identifier"
                    {...register('identifier', {
                      required: t('auth.emailOrPhone') + ' is required',
                    })}
                    type="text"
                    autoComplete="username"
                    className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    placeholder={t('auth.emailOrPhonePlaceholder')}
                  />
                </div>
                {errors.identifier && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.identifier.message}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    {t('common.password')}
                  </label>
                  <a href="#" className="text-sm font-medium text-primary-600 hover:text-primary-500">
                    {t('auth.forgotPassword')}
                  </a>
                </div>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden />
                  <input
                    id="password"
                    {...register('password', {
                      required: t('common.password') + ' is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters',
                      },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder:text-gray-400 transition focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                    placeholder={t('auth.passwordPlaceholder')}
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 shrink-0" aria-hidden />
                    ) : (
                      <Eye className="h-5 w-5 shrink-0" aria-hidden />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={authActionLoading}
                className="group relative flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {authActionLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                ) : (
                  <>
                    {t('auth.loginButton')}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                  </>
                )}
              </button>
            </form>

            <p className="mt-8 text-center text-sm text-gray-600">
              {t('auth.contactPrompt')}{' '}
              <Link to="/contact" className="font-semibold text-primary-600 hover:text-primary-500">
                {t('auth.contactUs')}
              </Link>
            </p>

            <div className="mt-6 border-t border-gray-100 pt-6 text-center">
              <a
                href="https://vuenexa.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-primary-600"
              >
                <Globe className="h-4 w-4" aria-hidden />
                vuenexa.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
